/**
 * Job preview is driven entirely by a `?preview=<jobId>` search param (RM-118),
 * so it works uniformly on any route that declares it (Jobs list, Admin) and is
 * deep-linkable. Reading/writing the param goes through this hook; the
 * `<JobPreviewSheet>` renders whenever `preview` is present.
 */
import { useNavigate, useSearch } from '@tanstack/react-router'

export function useJobPreview() {
  const nav = useNavigate()
  const preview = useSearch({
    strict: false,
    select: (s: Record<string, unknown>) =>
      typeof s.preview === 'string' && s.preview !== '' ? s.preview : undefined,
  })

  // `to: '.'` keeps the current route (works on /jobs and /admin/machines);
  // the search reducer merges the preview param. Cast the reducer return — a
  // route-agnostic navigate can't statically know each route's search shape.
  const setPreview = (id: string | undefined) =>
    void nav({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, preview: id }),
    } as never)
  const open = (id: string) => setPreview(id)
  const close = () => setPreview(undefined)

  return { preview, open, close }
}

/** Open/close state for the New Render sheet, driven by `?new=1`. */
export function useNewJobSheet() {
  const nav = useNavigate()
  const isOpen = useSearch({
    strict: false,
    select: (s: Record<string, unknown>) => s.new === true,
  })
  const set = (next: boolean) =>
    void nav({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        new: next || undefined,
      }),
    } as never)
  return { isOpen, open: () => set(true), close: () => set(false) }
}
